import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tab, Nav, Table, ProgressBar, Col, Image } from 'react-bootstrap';
import { AnnouncementModal } from 'components/common/Modals';
import Flex from 'components/common/Flex';
import ListingDetailsMedia from './ListingDetailsMedia';
import {
  SUCCESS,
  TREE_AREA,
  TREE_HEIGHT_DISTRIBUTION,
  CARBON_STOCK_ESTIMATION
} from 'helpers/constant';

const ListingDetailsFooter = () => {
  const { restURI } = useSelector(state => state.config);
  const { introduction, tasks, area } = useSelector(state => state.viewListing);

  const treeAreaRatio = tasks?.find(
    t => t.task === TREE_AREA && t.status == SUCCESS
  )?.result?.treeAreaRatio;
  const treeHeightDistribution = tasks?.find(
    t => t.task === TREE_HEIGHT_DISTRIBUTION && t.status == SUCCESS
  )?.result;
  const carbonStockEstimation = tasks?.find(
    t => t.task === CARBON_STOCK_ESTIMATION && t.status == SUCCESS
  )?.result;

  const { images } = useSelector(state => state.viewListing);

  const [treeHeightDistGraphVisible, setTreeHeightDistGraphVisible] =
    useState(false);

  return (
    <>
      <div className="mt-4 h-100">
        <Tab.Container defaultActiveKey="introduction">
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link
                eventKey="introduction"
                className="ps-0 cursor-pointer outline-none"
              >
                Introduction
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="specifications"
                className="px-2 px-md-3 cursor-pointer outline-none"
              >
                Specifications
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="photos"
                className="px-2 px-md-3 cursor-pointer outline-none"
              >
                Uploaded Photos
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="introduction">{introduction}</Tab.Pane>
            <Tab.Pane eventKey="specifications">
              <Table borderless responsive className="mb-0 fs-10">
                <thead className="bg-body-tertiary">
                  <tr>
                    <th className="text-900"></th>
                    <th
                      className="pe-x1 text-900 text-end"
                      style={{ width: '16rem' }}
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-bottom border-200">
                    <td>
                      <Flex alignItems="center" className="position-relative">
                        <div className="ms-3">
                          <h6 className="mb-1 fw-semibold">Total area</h6>
                        </div>
                      </Flex>
                    </td>
                    <td className="align-middle pe-x1">
                      {area && <>{area.toFixed(2)} ha</>}
                    </td>
                  </tr>
                  <tr className="border-bottom border-200">
                    <td>
                      <Flex alignItems="center" className="position-relative">
                        <div className="ms-3">
                          <h6 className="mb-1 fw-semibold">Tree coverage</h6>
                        </div>
                      </Flex>
                    </td>
                    <td className="align-middle pe-x1">
                      {treeAreaRatio && area && (
                        <Flex alignItems="center">
                          <ProgressBar
                            now={treeAreaRatio * 100}
                            style={{ width: '80px', height: 5 }}
                          />
                          <div className="fw-semibold ms-3">
                            {Math.round(treeAreaRatio * 100)}%
                          </div>
                        </Flex>
                      )}
                    </td>
                  </tr>
                  <tr className="border-bottom border-200">
                    <td>
                      <Flex alignItems="center" className="position-relative">
                        <div className="ms-3">
                          <h6 className="mb-1 fw-semibold">
                            Tree coverage area
                          </h6>
                        </div>
                      </Flex>
                    </td>
                    <td className="align-middle pe-x1">
                      {treeAreaRatio && (
                        <>{(treeAreaRatio * area).toFixed(2)} ha</>
                      )}
                    </td>
                  </tr>
                  {treeHeightDistribution && (
                    <>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Min tree height
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {treeHeightDistribution?.min.toFixed(2)} m
                          </Flex>
                        </td>
                      </tr>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Max tree height
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {treeHeightDistribution?.max.toFixed(2)} m
                          </Flex>
                        </td>
                      </tr>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Mean tree height
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {treeHeightDistribution?.mean.toFixed(2)} m
                          </Flex>
                        </td>
                      </tr>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Median tree height
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {treeHeightDistribution?.median.toFixed(2)} m
                          </Flex>
                        </td>
                      </tr>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Tree height distribution
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Link
                            className="text-1100"
                            to="#!"
                            onClick={() => setTreeHeightDistGraphVisible(true)}
                          >
                            open graph
                          </Link>
                        </td>
                      </tr>
                    </>
                  )}
                  {carbonStockEstimation && (
                    <>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">Carbon stock</h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {(carbonStockEstimation?.carbonStock / 100).toFixed(
                              2
                            )}{' '}
                            tonnes
                          </Flex>
                        </td>
                      </tr>
                      <tr className="border-bottom border-200">
                        <td>
                          <Flex
                            alignItems="center"
                            className="position-relative"
                          >
                            <div className="ms-3">
                              <h6 className="mb-1 fw-semibold">
                                Carbon stock (per ha)
                              </h6>
                            </div>
                          </Flex>
                        </td>
                        <td className="align-middle pe-x1">
                          <Flex alignItems="center">
                            {(
                              carbonStockEstimation?.carbonStock /
                              100 /
                              (treeAreaRatio * area)
                            ).toFixed(2)}{' '}
                            tonnes
                          </Flex>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </Tab.Pane>
            <Tab.Pane eventKey="photos">
              {images.length > 0 && (
                <Col className="mb-3 mb-lg-0">
                  <ListingDetailsMedia images={images} />
                </Col>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
      <AnnouncementModal
        show={treeHeightDistGraphVisible}
        onClose={() => setTreeHeightDistGraphVisible(false)}
        size="md"
        title="Tree height distribution"
        content={
          treeHeightDistribution ? (
            <Image
              fluid
              className="fit-cover w-sm-100 h-sm-100 rounded"
              src={`${restURI}/files/${treeHeightDistribution.plotFile}`}
            />
          ) : (
            <div></div>
          )
        }
      />
    </>
  );
};

export default ListingDetailsFooter;
